import { useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { IMergeTag } from '@beefree.io/sdk/dist/types/bee';
import { getEngageConfiguration } from 'api/engage';
import { transformEngageTags } from 'assets/js/pages/engage/util';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';

interface MergeTagFormValues {
    category: string;
    tag: string;
}

interface MergeTagsDialogProps extends Dialog.DialogProps {
    onCancel: () => void;
    onSelect: (tag: IMergeTag) => void;
}

const MergeTagsDialog = ({ onCancel, onSelect, ...rest }: MergeTagsDialogProps) => {
    const { accountId } = useCurrentAccount();

    const { data: mergeTagsConfig } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageConfiguration(accountId),
        queryFn: () => getEngageConfiguration(accountId),
        select: data => ({
            prefix: data.tags.prefix,
            suffix: data.tags.suffix,
            mergeTags: transformEngageTags(data.tags.items),
        }),
        staleTime: Infinity,
    });

    const methods = Form.useGivebutterForm<MergeTagFormValues>({
        defaultValues: {
            category: mergeTagsConfig.mergeTags[0].title ?? '',
            tag: '',
        },
    });
    const { control, watch, handleSubmit, path, reset, setValue } = methods;

    const selectedCategory = watch('category');
    const categories = mergeTagsConfig.mergeTags;
    const tags = useMemo(() => {
        const foundCat = categories.find(c => c.title === selectedCategory);
        if (!foundCat) return [];

        return foundCat.menu ?? [];
    }, [selectedCategory, categories]);

    const onFormSubmit: SubmitHandler<MergeTagFormValues> = values => {
        const foundTag = tags.find(t => t.value === values.tag);
        if (!values.tag || !foundTag) return;

        onSelect({
            name: foundTag.title ?? '',
            value: `${mergeTagsConfig.prefix}${foundTag.value}${mergeTagsConfig.suffix}`,
        });
        reset();
    };

    return (
        <Dialog.Root {...rest}>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content
                    onInteractOutside={() => onCancel()}
                    onPointerDownOutside={() => onCancel()}
                    onEscapeKeyDown={() => onCancel()}
                >
                    <Form.Provider {...methods}>
                        <Dialog.Body>
                            <form
                                onSubmit={handleSubmit(onFormSubmit)}
                                className="flex flex-col gap-4"
                            >
                                <Dialog.Title className="sm:text-2xl">
                                    Choose merge tag
                                </Dialog.Title>
                                <Form.Container>
                                    <Form.Label name={path('category')}>Category</Form.Label>
                                    <Form.SingleSelect
                                        control={control}
                                        name="category"
                                        onValueChange={() => setValue('tag', '')}
                                    >
                                        <SingleSelect.Trigger aria-label="Category">
                                            <SingleSelect.Value placeholder="Select category" />
                                        </SingleSelect.Trigger>
                                        <SingleSelect.Content>
                                            {categories.map(item => (
                                                <SingleSelect.Item
                                                    key={item.title}
                                                    value={item.title}
                                                >
                                                    <SingleSelect.ItemText>
                                                        {item.title}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                    <Form.Error name={path('category')} />
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('tag')}>Merge tag</Form.Label>
                                    <Form.SingleSelect
                                        control={control}
                                        name="tag"
                                        rules={{ required: 'Please select a merge tag.' }}
                                    >
                                        <SingleSelect.Trigger aria-label="Merge tag">
                                            <SingleSelect.Value placeholder="Select merge tag" />
                                        </SingleSelect.Trigger>
                                        <SingleSelect.Content>
                                            {tags.map(tag => (
                                                <SingleSelect.Item
                                                    value={tag.value!}
                                                    key={tag.value}
                                                >
                                                    <SingleSelect.ItemText>
                                                        {tag.title}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                    <Form.Error name={path('tag')} />
                                </Form.Container>
                                <div className="flex justify-end gap-4">
                                    <Dialog.Cancel
                                        onClick={() => {
                                            onCancel();
                                            reset();
                                        }}
                                    >
                                        Cancel
                                    </Dialog.Cancel>
                                    <Form.SubmitButton>Insert merge tag</Form.SubmitButton>
                                </div>
                            </form>
                        </Dialog.Body>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export { MergeTagsDialog };
